import { SelectContent, SelectItem, SelectTrigger, SelectValue } from '@HoudiniUI/Select';
import { useAdminUsersQuery } from '@Queries/index';
import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import React from 'react';
import { toast } from 'sonner';
import { useUserSession } from '../../../../../../../../models/hooks/appStateHooks';
import { useGetExternalIntegrationOAuthClientDisplay } from '../../../../../../../../queries/Integrations/useGetExternalIntegrationOAuthClientDisplay';
import { usePostExternalIntegrationOAuthClientMutation } from '../../../../../../../../queries/Integrations/usePostExternalIntegrationOAuthClient';
import { AccountUserViewModel } from '../../../../../../../../viewmodels/AdminViewModels';
import { Button } from '../../../../../../../../web/components-v2/Button';
import { Modal } from '../../../../../../../../web/components/Modal';
import { DangerIcon } from '../../../../../../../../web/components/svgs/icons/DangerIcon';
import { Input } from '@HoudiniUI/Input';
import { baseStyleSheet } from '../../../../../../../../web/styles/styles';
import { EditModeSelect, useEditMode } from '../context';
import { getProviderConfig } from '../providers';
import { styleSheet } from '../styles';
import { SettingGroup } from './SettingGroup';
import { SettingRow } from './SettingRow';

const secretPlaceholder = '***************';

interface IConfigurationSettingsGroupProps {
	impersonationContext: Api.IImpersonationContext;
	configuration: Api.IIntegrationConfiguration;
	provider: Api.IntegrationProvider;
	setConfiguration: (configuration: Api.IIntegrationConfiguration) => void;
}

export const ConfigurationSettingsGroup: React.FC<IConfigurationSettingsGroupProps> = ({
	configuration,
	impersonationContext,
	provider,
	setConfiguration,
}) => {
	const [syncUsers, setSyncUsers] = React.useState<AccountUserViewModel[]>([]);
	const [clientId, setClientId] = React.useState('');
	const [clientSecret, setClientSecret] = React.useState('');
	const [isClientCredentialsModalOpen, setIsClientCredentialsModalOpen] = React.useState(false);

	const userSession = useUserSession();

	const { isEditing } = useEditMode();

	// Get provider config
	const providerConfig = getProviderConfig(provider);
	const configSchema = providerConfig.configuration;

	const onUpdate = (updatedConfiguration: Partial<Api.IIntegrationConfiguration>) => {
		setConfiguration({
			...configuration,
			...updatedConfiguration,
		});
	};

	const error = syncUsers?.length > 0 ? null : '(No user connections to sync)';

	const getAdminsQuery = useAdminUsersQuery({
		impersonationContext,
	});

	React.useEffect(() => {
		if (!getAdminsQuery?.data) {
			return;
		}

		const users: AccountUserViewModel[] = [];

		// Determine sync user source from config (default to 'connectedUsers')
		const syncUserSource = configSchema?.syncUserSource ?? 'connectedUsers';

		if (syncUserSource === 'allAdmins') {
			// All admin users are eligible (for providers without user connections)
			getAdminsQuery.data.forEach(u => users.push(new AccountUserViewModel(userSession, u)));
		} else {
			// Only users with active connections are eligible
			configuration.userConnections
				?.filter(c => c.isConnected)
				?.forEach(u => {
					const user = getAdminsQuery.data?.find(x => x.id === u.userId);

					if (user) {
						const admin = new AccountUserViewModel(userSession, user);
						users.push(admin);
					}
				});
		}

		setSyncUsers(users);
	}, [getAdminsQuery.data, configuration.userConnections, userSession, configSchema?.syncUserSource]);

	const oAuthClientMutation = usePostExternalIntegrationOAuthClientMutation({
		impersonationContext,
		onSuccess: () => {
			setIsClientCredentialsModalOpen(false);
			toast.success('Successfully saved the client credentials.');
		},
	});

	const externalIntegrationOAuthClientDisplayQuery = useGetExternalIntegrationOAuthClientDisplay({
		enabled: !!configSchema?.showOAuthClientCredentials,
		integrationProvider: provider,
		impersonationContext,
	});

	React.useEffect(() => {
		if (configuration?.encryptedClientKeyId) {
			setClientId(externalIntegrationOAuthClientDisplayQuery?.data?.clientId);
			setClientSecret(secretPlaceholder);
		}
	}, [configuration?.encryptedClientKeyId, externalIntegrationOAuthClientDisplayQuery?.data?.clientId]);

	const configureCredentials = async () => {
		if (clientId !== externalIntegrationOAuthClientDisplayQuery?.data?.clientId || clientSecret !== secretPlaceholder) {
			await oAuthClientMutation.mutateAsync({
				integrationProvider: provider,
				clientId,
				clientSecret,
			});

			return;
		}

		if (clientSecret) {
			setClientSecret(secretPlaceholder);
		}

		setIsClientCredentialsModalOpen(false);
	};

	const onSaveClientCredentials = () => {
		configureCredentials();
	};
	const syncUserOptions = syncUsers.map(user => {
		return {
			label: user.name,
			value: user.id,
		};
	});

	if (configSchema?.showSyncUser === false) {
		return null;
	}

	return (
		<SettingGroup title='Configuration'>
			<SettingRow
				id={`requirement-target-${provider}-sync-user`}
				title='Sync User'
				subtitle='User credentials to be used for all sync operations'
				error={error}
			>
				<EditModeSelect
					items={syncUserOptions}
					value={configuration.syncUserId}
					displayValue={
						syncUsers.find(u => u.id === configuration.syncUserId)
							? syncUsers.find(u => u.id === configuration.syncUserId)?.name
							: 'Select sync user'
					}
					onValueChange={(value: string) => onUpdate({ syncUserId: value })}
				>
					<SelectTrigger styles={styleSheet.editableField}>
						<SelectValue placeholder='Select sync user' />
					</SelectTrigger>
					<SelectContent>
						{syncUserOptions.map(o => {
							return (
								<SelectItem key={o.value} value={o.value}>
									{o.label}
								</SelectItem>
							);
						})}
					</SelectContent>
				</EditModeSelect>
			</SettingRow>
			{configSchema?.showOAuthClientCredentials && (
				<SettingRow
					id={`requirement-target-${provider}-client-credentials`}
					title='Client Credentials'
					subtitle='Credentials used for accessing the integration API'
				>
					{isEditing ? (
						<Button variant='primary' onClick={() => setIsClientCredentialsModalOpen(true)}>
							Manage Client Credentials
						</Button>
					) : (
						<span className={css(styleSheet.readOnlyField)}>{clientId ? clientId : 'Credentials not configured'}</span>
					)}
				</SettingRow>
			)}

			<Modal
				isOpen={isClientCredentialsModalOpen}
				onRequestClose={() => setIsClientCredentialsModalOpen(false)}
				useDefaultHeader={true}
				className={css(styleSheet.clientCredentialsModal)}
			>
				<span className={css(styleSheet.modalTitle)}>Enter your client credentials</span>
				<div className={css(baseStyleSheet.mt2)}>
					<span>Client ID</span>
					<div>
						<Input
							autoComplete='off'
							autoFocus={true}
							id='client-credentials-client-id'
							onChange={ev => setClientId(ev.target.value)}
							type='text'
							value={clientId}
						/>
					</div>
				</div>

				<div className={css(baseStyleSheet.mt2)}>
					<span>Client Secret</span>
					<div>
						<Input
							autoComplete='off'
							id='client-credentials-client-secret'
							onChange={ev => setClientSecret(ev.target.value)}
							type='text'
							value={clientSecret}
						/>
					</div>
				</div>

				{oAuthClientMutation.isError && (
					<div className={css(styleSheet.modalError, baseStyleSheet.mt2)}>
						<span className={css(styleSheet.modalDangerIcon)}>
							<DangerIcon />
						</span>
						<span>{oAuthClientMutation.error?.systemMessage}</span>
					</div>
				)}
				<div className={css(baseStyleSheet.mt2)}>
					<Button onClick={onSaveClientCredentials} variant='primary' disabled={oAuthClientMutation.isPending}>
						Save
					</Button>
				</div>
			</Modal>
		</SettingGroup>
	);
};
